import React, { useState, useRef } from "react";
import axiosAPI from "Includes/api/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  FileUpload,
  Close,
  CheckCircle,
  WarningAmber,
  School,
  HelpOutline,
  Refresh,
  Link as LinkIcon,
} from "@mui/icons-material";

const HistoricalImportModal = ({ open, onClose, onImportSuccess }) => {
  const [step, setStep] = useState("upload"); // upload | preview | success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [createNewSchools, setCreateNewSchools] = useState(true);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [gmapsUrl, setGmapsUrl] = useState("");
  const [filterPreviewTag, setFilterPreviewTag] = useState("all");
  const fileInputRef = useRef(null);

  const resetState = () => {
    setStep("upload");
    setLoading(false);
    setError("");
    setPreviewData(null);
    setCreateNewSchools(true);
    setSkipDuplicates(true);
    setImportResult(null);
    setFilterPreviewTag("all");
    setGmapsUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleLoadGoogleMapsUrl = async () => {
    const trimmed = (gmapsUrl || "").trim();
    if (!trimmed) {
      setError("Please paste a valid Google Maps shared list link (e.g. https://maps.app.goo.gl/...).");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const resp = await axiosAPI.post("/api/area-search/historical/preview/", {
        google_maps_url: trimmed,
      });
      if (resp.data?.success) {
        setPreviewData(resp.data);
        setStep("preview");
      } else {
        setError(resp.data?.error || "Failed to fetch Google Maps link data.");
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.error ||
        e.message ||
        "Could not load data from Google Maps link. Please verify the link is public and accessible."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (step === "success" && onImportSuccess) {
      onImportSuccess();
    }
    resetState();
    onClose();
  };

  const handleFileChange = async (selectedFile) => {
    if (!selectedFile) return;
    const name = selectedFile.name.toLowerCase();
    if (
      !name.endsWith(".kml") &&
      !name.endsWith(".kmz") &&
      !name.endsWith(".csv") &&
      !name.endsWith(".json") &&
      !name.endsWith(".geojson")
    ) {
      setError("Please select a supported file (.csv, .json, .geojson, .kml, or .kmz).");
      return;
    }
    if (selectedFile.size > 15 * 1024 * 1024) {
      setError("File exceeds 15MB limit. Please upload a smaller export file.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const resp = await axiosAPI.post("/api/area-search/historical/preview/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (resp.data?.success) {
        setPreviewData(resp.data);
        setStep("preview");
      } else {
        setError(resp.data?.error || "Failed to parse file.");
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.error ||
        e.message ||
        "Could not process export file. Ensure it is a valid KML, KMZ, or CSV."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData || !previewData.preview_records) return;
    setLoading(true);
    setError("");

    try {
      const resp = await axiosAPI.post("/api/area-search/historical/confirm/", {
        records: previewData.preview_records,
        create_new_schools: createNewSchools,
        skip_duplicates: skipDuplicates,
      });

      if (resp.data?.success) {
        setImportResult(resp.data);
        setStep("success");
      } else {
        setError(resp.data?.error || "Import failed. Please try again.");
      }
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || e.message || "Failed to commit historical records.");
    } finally {
      setLoading(false);
    }
  };

  const filteredPreviewList = (previewData?.preview_records || []).filter((rec) => {
    if (filterPreviewTag === "all") return true;
    if (filterPreviewTag === "matched") return rec.status_tag === "matched";
    if (filterPreviewTag === "new") return rec.status_tag === "new";
    if (filterPreviewTag === "duplicate") return rec.is_duplicate;
    if (filterPreviewTag === "invalid") return !rec.has_valid_location;
    return true;
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e2e8f0",
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FileUpload sx={{ color: "#2563eb", fontSize: 22 }} />
          <div>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#1e293b" }}>
              Import Previous Sales Data
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
              Bring legacy Google Maps markers, emojis, and sales records into All Schools
            </Typography>
          </div>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, maxHeight: "70vh", overflowY: "auto" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: 13 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* STEP 1: Upload */}
        {step === "upload" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Instruction Banner */}
            <Alert severity="info" icon={<HelpOutline fontSize="inherit" />} sx={{ fontSize: 12.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>
                Exporting from Google Maps / Google My Maps:
              </Typography>
              Standard Google Maps web links (e.g. <code>maps/@12.94...</code>) only define viewport coordinates and do not expose private saved markers. To import your team's historical data:
              <ol style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                <li>Open your team's map at <strong>Google My Maps</strong> (<code>google.com/maps/d/</code>).</li>
                <li>Click the 3-dots menu on the left panel &rarr; select <strong>Export to KML/KMZ</strong> (or export as <strong>CSV</strong>).</li>
                <li>Upload the downloaded file below.</li>
              </ol>
            </Alert>

            {/* Direct Google Maps Link Input Option */}
            <Paper
              variant="outlined"
              sx={{
                p: 2.2,
                borderRadius: 2,
                background: "#ffffff",
                border: "1.5px solid #cbd5e1",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: "#1e293b", display: "flex", alignItems: "center", gap: 0.8 }}>
                  <LinkIcon sx={{ color: "#2563eb", fontSize: 18 }} />
                  Import Directly from Google Maps Link
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
                  Paste any Google Maps shared list link (e.g. <code>https://maps.app.goo.gl/...</code>) to automatically extract places, coordinates, emojis, and notes.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="https://maps.app.goo.gl/cikvcyCCynLM5bd29"
                  value={gmapsUrl}
                  onChange={(e) => setGmapsUrl(e.target.value)}
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleLoadGoogleMapsUrl();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: 13,
                      backgroundColor: "#f8fafc",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={loading || !gmapsUrl.trim()}
                  onClick={handleLoadGoogleMapsUrl}
                  sx={{
                    backgroundColor: "#2563eb",
                    fontWeight: 600,
                    fontSize: 12,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    px: 2.2,
                    py: 1,
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#1d4ed8" },
                  }}
                >
                  Fetch & Preview
                </Button>
              </Box>
            </Paper>

            <Box sx={{ display: "flex", alignItems: "center", my: 0.5 }}>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }} />
              <Typography sx={{ px: 2, fontSize: 11.5, color: "#94a3b8", fontWeight: 600 }}>OR UPLOAD FILE (.CSV, .JSON, .KML)</Typography>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }} />
            </Box>

            {/* Dropzone */}
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: "center",
                border: "2px dashed #93c5fd",
                backgroundColor: "#f8fafc",
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { backgroundColor: "#eff6ff", borderColor: "#3b82f6" },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.json,.geojson,.kml,.kmz"
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e.target.files?.[0])}
              />
              {loading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={32} />
                  <Typography sx={{ fontSize: 13, color: "#2563eb", fontWeight: 600 }}>
                    Analyzing map records, emojis, and matching schools...
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <FileUpload sx={{ fontSize: 44, color: "#3b82f6" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>
                    Click or drag & drop to select export file
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                    Supported formats: <strong>.csv</strong>, <strong>.json</strong>, <strong>.geojson</strong>, <strong>.kml</strong>, or <strong>.kmz</strong> (max 25MB)
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* STEP 2: Preview */}
        {step === "preview" && previewData && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Header info */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: "#1e293b" }}>
                  File: {previewData.filename}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  Format: {previewData.file_type?.toUpperCase()} · {previewData.total_records} records found
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Refresh sx={{ fontSize: 14 }} />}
                onClick={() => setStep("upload")}
                sx={{ textTransform: "none", fontSize: 11.5 }}
              >
                Choose Another File
              </Button>
            </Box>

            {/* Stat Badges */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
                gap: 1,
              }}
            >
              <Paper
                variant="outlined"
                onClick={() => setFilterPreviewTag("all")}
                sx={{
                  p: 1.25,
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: 1.5,
                  borderColor: filterPreviewTag === "all" ? "#2563eb" : "#e2e8f0",
                  backgroundColor: filterPreviewTag === "all" ? "#eff6ff" : "#fff",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
                  {previewData.total_records}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Total Found</Typography>
              </Paper>

              <Paper
                variant="outlined"
                onClick={() => setFilterPreviewTag("matched")}
                sx={{
                  p: 1.25,
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: 1.5,
                  borderColor: filterPreviewTag === "matched" ? "#059669" : "#e2e8f0",
                  backgroundColor: filterPreviewTag === "matched" ? "#ecfdf5" : "#fff",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#059669" }}>
                  {previewData.matched_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#065f46", fontWeight: 600 }}>Matched Schools</Typography>
              </Paper>

              <Paper
                variant="outlined"
                onClick={() => setFilterPreviewTag("new")}
                sx={{
                  p: 1.25,
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: 1.5,
                  borderColor: filterPreviewTag === "new" ? "#0284c7" : "#e2e8f0",
                  backgroundColor: filterPreviewTag === "new" ? "#f0f9ff" : "#fff",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0284c7" }}>
                  {previewData.new_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#0369a1", fontWeight: 600 }}>New Schools</Typography>
              </Paper>

              <Paper
                variant="outlined"
                onClick={() => setFilterPreviewTag("duplicate")}
                sx={{
                  p: 1.25,
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: 1.5,
                  borderColor: filterPreviewTag === "duplicate" ? "#d97706" : "#e2e8f0",
                  backgroundColor: filterPreviewTag === "duplicate" ? "#fffbeb" : "#fff",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#d97706" }}>
                  {previewData.duplicate_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#92400e", fontWeight: 600 }}>Duplicates</Typography>
              </Paper>

              <Paper
                variant="outlined"
                onClick={() => setFilterPreviewTag("invalid")}
                sx={{
                  p: 1.25,
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: 1.5,
                  borderColor: filterPreviewTag === "invalid" ? "#dc2626" : "#e2e8f0",
                  backgroundColor: filterPreviewTag === "invalid" ? "#fef2f2" : "#fff",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#dc2626" }}>
                  {previewData.invalid_location_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#991b1b", fontWeight: 600 }}>Invalid Location</Typography>
              </Paper>
            </Box>

            {/* Options */}
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", p: 1, backgroundColor: "#f8fafc", borderRadius: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={createNewSchools}
                    onChange={(e) => setCreateNewSchools(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12.5, color: "#1e293b", fontWeight: 500 }}>
                    Create new prospect school records for unmatched markers
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12.5, color: "#1e293b", fontWeight: 500 }}>
                    Skip duplicate records safely
                  </Typography>
                }
              />
            </Box>

            {/* Records Preview Table */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Marker / Emoji</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>School Name</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Original Status</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Match Status</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Salesperson / Date</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPreviewList.slice(0, 100).map((rec, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: 13, textAlign: "center", width: 60 }}>
                        {rec.emoji ? (
                          <span style={{ fontSize: 16 }}>{rec.emoji}</span>
                        ) : rec.marker_icon ? (
                          <Tooltip title={rec.marker_icon}>
                            <span style={{ fontSize: 10, color: "#64748b" }}>📍</span>
                          </Tooltip>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                        {rec.school_name}
                        {rec.address && (
                          <Typography sx={{ fontSize: 10.5, color: "#64748b" }}>
                            {rec.address}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5 }}>
                        {rec.sales_status ? (
                          <Chip
                            size="small"
                            label={rec.sales_status}
                            sx={{ height: 20, fontSize: 10.5, fontWeight: 600 }}
                          />
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5 }}>
                        {rec.matched_school ? (
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: "13px !important" }} />}
                            label={`Matched: #${rec.matched_school.id}`}
                            size="small"
                            color="success"
                            sx={{ height: 20, fontSize: 10.5 }}
                          />
                        ) : rec.is_duplicate ? (
                          <Chip
                            icon={<WarningAmber sx={{ fontSize: "13px !important" }} />}
                            label="Duplicate"
                            size="small"
                            color="warning"
                            sx={{ height: 20, fontSize: 10.5 }}
                          />
                        ) : (
                          <Chip
                            icon={<School sx={{ fontSize: "13px !important" }} />}
                            label="New Prospect"
                            size="small"
                            color="info"
                            sx={{ height: 20, fontSize: 10.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: "#475569" }}>
                        {rec.salesperson_name || "—"}
                        {rec.previous_visit_date && (
                          <Typography sx={{ fontSize: 10, color: "#64748b" }}>
                            {rec.previous_visit_date}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 11,
                          color: "#64748b",
                          maxWidth: 160,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {rec.notes || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredPreviewList.length > 100 && (
              <Typography sx={{ fontSize: 11, color: "#64748b", textAlign: "center" }}>
                Showing first 100 of {filteredPreviewList.length} records in preview.
              </Typography>
            )}
          </Box>
        )}

        {/* STEP 3: Success */}
        {step === "success" && importResult && (
          <Box sx={{ textAlign: "center", py: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <CheckCircle sx={{ fontSize: 56, color: "#059669" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#1e293b" }}>
              Historical Data Imported Successfully!
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", maxWidth: 440 }}>
              Historical sales records, original emojis, markers, and statuses are now stored persistently in the database and visible to authorized users.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
                mt: 1,
                p: 2,
                backgroundColor: "#f8fafc",
                borderRadius: 2,
                border: "1px solid #e2e8f0",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#059669" }}>
                  {importResult.imported_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>Records Saved</Typography>
              </Box>
              <Box sx={{ width: 1, bgcolor: "#e2e8f0" }} />
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#2563eb" }}>
                  {importResult.matched_schools_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>Matched Schools</Typography>
              </Box>
              <Box sx={{ width: 1, bgcolor: "#e2e8f0" }} />
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#7c3aed" }}>
                  {importResult.new_schools_created_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>New Schools Created</Typography>
              </Box>
              <Box sx={{ width: 1, bgcolor: "#e2e8f0" }} />
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#d97706" }}>
                  {importResult.skipped_duplicates_count}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>Duplicates Skipped</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #e2e8f0" }}>
        {step === "upload" && (
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        )}

        {step === "preview" && (
          <>
            <Button onClick={() => setStep("upload")} sx={{ textTransform: "none" }} disabled={loading}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmImport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                background: "#2563eb",
                "&:hover": { background: "#1d4ed8" },
              }}
            >
              {loading ? "Importing Records..." : `Confirm & Import ${previewData?.total_records || ""} Records`}
            </Button>
          </>
        )}

        {step === "success" && (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              background: "#059669",
              "&:hover": { background: "#047857" },
            }}
          >
            Done & View in All Schools
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default HistoricalImportModal;
